import { useEffect, useState } from 'react'

import { API } from '@/api'
import { Trans } from '@/components/Trans'
import { PurchasedEIP } from '@/models/eip'
import { InstanceStatus } from '@/models/instance'
import { formatBitsPerSec } from '@/utils/formatter'

interface InformationContentProps {
  eip: PurchasedEIP
  regionDisplayName: string
}

export function InformationContent({ eip, regionDisplayName }: InformationContentProps) {
  const [instanceCount, setInstanceCount] = useState<number>(0)

  useEffect(() => {
    let active = true
    const fetchCount = async () => {
      try {
        const res = await API.getNodeList({
          page: 1,
          per_page: 500,
          statuses: String(InstanceStatus.Running),
          region_filter_criteria: 0,
          regions: [eip.domain],
        })
        if (res.code === 0 && res.userNodes && active) {
          const count = res.userNodes.filter((node) =>
            node.nats?.some((n) => n.net.netResID === eip.resID),
          ).length
          setInstanceCount(count)
        }
      } catch (e) {
        console.error(e)
      }
    }
    fetchCount()
    return () => {
      active = false
    }
  }, [eip.resID, eip.domain])

  return (
    <div className="bg-neutral-50 rounded-lg p-4 flex flex-col gap-4">
      <div className="grid grid-cols-5 gap-4">
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.infoIPAddress">
              IP Address
            </Trans>
          </div>
          <div className="t-sb text-primary">{eip.name || '-'}</div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.infoStatus">
              Status
            </Trans>
          </div>
          <div className="t-sb text-active">
            <Trans ns="eip" i18nKey="statusAvailable">
              Available
            </Trans>
          </div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.infoRegion">
              Region
            </Trans>
          </div>
          <div className="t-sb text-primary">{regionDisplayName}</div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.infoRelatedResource">
              Related Resource
            </Trans>
          </div>
          <div className="t-sb text-primary">
            <span className="text-tertiary">instance</span> {instanceCount}
          </div>
        </div>
        <div>
          <div className="t-xs text-secondary mb-1">
            <Trans ns="eip" i18nKey="detail.infoBandwidthCap">
              Bandwidth Cap
            </Trans>
          </div>
          <div className="t-sb text-primary">
            {!eip.bandwidth ? (
              '-'
            ) : eip.bandwidth.bandwidthType === 0 ? (
              <Trans ns="eip" i18nKey="bandwidthShared">
                Shared
              </Trans>
            ) : (
              formatBitsPerSec(eip.bandwidth.rateBitsPerSec)
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
